import React, { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import ProductCard from '../components/ProductCard';
import { FavoriteButton } from '../system-modules/favorites/components';

export default function HubStorefront() {
  const { slug } = useParams();
  const [hub, setHub] = useState(null);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchHub = async () => {
      try {
        setLoading(true);

        // Get hub data
        const hubRes = await fetch(`/api/users/${slug}`);
        if (!hubRes.ok) throw new Error('Hub not found');
        const hubData = await hubRes.json();
        setHub(hubData);

        // Get hub products - using the unified API
        const productsRes = await fetch(`/api/products?hubId=${hubData._id}`);
        if (productsRes.ok) {
          setProducts(await productsRes.json());
        } else {
          setProducts([]);
        }
      } catch (err) {
        console.error('Error fetching hub:', err);
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    if (slug) fetchHub();
  }, [slug]);

  if (loading) return <div className="text-center py-12">Loading hub store...</div>;
  if (error || !hub) return <div className="text-center py-12 text-red-500">Hub not found</div>;

  const storefront = hub.storefrontV2 || hub.storefront || {};
  const hubName = storefront.name || hub.hubSettings?.hubName || hub.name || 'Hub';
  const description = storefront.description || hub.hubSettings?.description || '';
  const tagline = storefront.tagline || hub.tagline || '';
  const bannerImage = storefront.bannerImage || hub.heroImageUrl || '';
  const logoUrl = storefront.logoUrl || hub.hubSettings?.logoUrl || hub.logoUrl || '';
  const primaryColor = storefront.primaryColor || '#16a34a';
  const teamMembers = storefront.teamMembers || [];

  return (
    <div className="min-h-screen">
      {bannerImage && (
        <div className="relative h-64 bg-gray-200">
          <img src={bannerImage} alt={hubName} className="w-full h-full object-cover" />
        </div>
      )}

      <div className="py-6 px-4" style={{ backgroundColor: primaryColor }}>
        <div className="max-w-6xl mx-auto flex items-center justify-between text-white">
          <div className="flex items-center gap-4">
            {logoUrl ? (
              <img src={logoUrl} alt={hubName} className="w-16 h-16 rounded-full object-cover bg-white p-1" />
            ) : (
              <div className="w-16 h-16 rounded-full bg-white/20 flex items-center justify-center text-2xl">🏪</div>
            )}
            <div>
              <h1 className="text-3xl font-bold">{hubName}</h1>
              {tagline && <p className="text-white/80 text-sm">{tagline}</p>}
              {description && <p className="text-white/60 text-xs mt-1">{description}</p>}
            </div>
          </div>
          <div className="flex items-center gap-4">
            <FavoriteButton vendorId={hub._id} size="md" showTooltip={true} />
            <span className="text-white/80 text-sm">{products.length} products</span>
          </div>
        </div>
      </div>

      {teamMembers.length > 0 && (
        <div className="max-w-6xl mx-auto p-6">
          <h2 className="text-xl font-semibold mb-4">Our Team</h2>
          <div className="flex flex-wrap gap-4">
            {teamMembers.map((member, index) => (
              <div key={index} className="bg-white rounded-lg shadow p-4 text-center min-w-[120px]">
                <div className="w-16 h-16 rounded-full bg-gray-200 mx-auto flex items-center justify-center text-2xl">
                  {member.imageUrl ? (
                    <img src={member.imageUrl} alt={member.name} className="w-full h-full rounded-full object-cover" />
                  ) : '👤'}
                </div>
                <p className="font-medium mt-2">{member.name}</p>
                <p className="text-sm text-gray-500">{member.role}</p>
              </div>
            ))}
          </div>
        </div>
      )}

      <div className="max-w-6xl mx-auto p-6">
        {products.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-500">No products available at this hub yet.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {products.map(product => (
              <ProductCard 
                key={product._id} 
                product={product} 
                hubId={hub._id} 
                hubName={hubName}
                hubSlug={hub.slug}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
